import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 3px solid #971b2f;
`;

const Title = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  font-weight: 700;
  color: #333;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 30px;
  line-height: 1.5;
`;

const InputContainer = styled.div`
  position: relative;
  margin-bottom: 30px;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid ${props => props.error ? '#dc3545' : '#e0e0e0'};
  border-radius: 12px;
  font-size: 16px;
  outline: none;
  transition: all 0.3s ease;
  text-align: center;
  letter-spacing: 2px;
  font-weight: 600;

  &:focus {
    border-color: #d4af37;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }

  &::placeholder {
    color: #999;
    letter-spacing: normal;
    font-weight: normal;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(212, 175, 55, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const LockIcon = styled(Lock)`
  width: 48px;
  height: 48px;
  color: #d4af37;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const SuccessMessage = styled.div`
  color: #28a745;
  font-size: 14px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const CodeValidation = ({ onValidationSuccess }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const VALIDATION_CODE = process.env.REACT_APP_VALIDATION_CODE || 'POD2024';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter the validation code');
      return;
    }

    setIsValidating(true);
    setError('');
    setSuccess('');

    setTimeout(() => {
      if (code.trim().toUpperCase() === VALIDATION_CODE.toUpperCase()) {
        setSuccess('Code validated successfully!');
        setIsValidating(false);
        
        localStorage.setItem('podValidationCode', code);
        
        setTimeout(() => {
          setIsVisible(false);
          onValidationSuccess();
        }, 1000);
      } else {
        setError('Invalid validation code. Please try again.');
        setIsValidating(false);
      }
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  useEffect(() => {
    const savedCode = localStorage.getItem('podValidationCode');
    if (savedCode && savedCode.toUpperCase() === VALIDATION_CODE.toUpperCase()) {
      setIsVisible(false);
      onValidationSuccess();
    }
  }, [onValidationSuccess, VALIDATION_CODE]);

  return (
    <AnimatePresence>
      {isVisible && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Modal
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <IconContainer>
              <LockIcon />
            </IconContainer>
            
            <Title>Access Required</Title>
            <Subtitle>
              Please enter the validation code to access the POD Management System
            </Subtitle>

            <form onSubmit={handleSubmit}>
              <InputContainer>
                <Input
                  type="text"
                  placeholder="Enter validation code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyPress={handleKeyPress}
                  error={!!error}
                  disabled={isValidating}
                  autoFocus
                />
              </InputContainer>

              {error && (
                <ErrorMessage>
                  <AlertCircle size={16} />
                  {error}
                </ErrorMessage>
              )}

              {success && (
                <SuccessMessage>
                  <CheckCircle size={16} />
                  {success}
                </SuccessMessage>
              )}

              <Button
                type="submit"
                disabled={isValidating}
              >
                {isValidating ? 'Validating...' : 'Validate Code'}
              </Button>
            </form>
          </Modal>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default CodeValidation;